import React, { useState, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import SweetAlert from "react-bootstrap-sweetalert"
import { AvForm, AvField } from "availity-reactstrap-validation"
import { useDispatch, useSelector } from "react-redux"
import { Link, withRouter } from "react-router-dom"
import { Row, Col, Button, Label } from "reactstrap"
import Select from "react-select"
import Switch from "react-switch"
import {
  addCustomer,
  addCustomerClean,
  addPosition,
  addPositionClean,
  editCustomer,
  editCustomerClean,
  editPosition,
  editPositionClean,
  getShoppingAreasV2,
} from "../../store/actions"
import {
  useGetEntitiesCmoQuery,
  useGetMunicipalitiesCmoQuery,
  useGetAtcsCmoQuery,
  useUpdateCustomerCmoMutation,
} from "../../store/api/customersApi"

const Offsymbol = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        fontSize: 10,
        color: "#fff",
        paddingRight: 2,
      }}
    >
      {" "}
      No
    </div>
  )
}

const OnSymbol = () => {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        fontSize: 10,
        color: "#fff",
        paddingRight: 2,
      }}
    >
      {" "}
      Sí
    </div>
  )
}

const CustomersForm = props => {
  const customer = props.customer
  const [formError, setFormError] = useState(false)
  const [status, setStatus] = useState(customer.statusId)
  const [shoppingAreasSelectedMulti, setShoppingAreasSelectedMulti] =
    useState(undefined)

  const [cmoShoppingArea, setCmoShoppingArea] = useState(
    customer.id_zona_comercial_cmo ?? null
  )

  // Entity selected by user (ID used to load municipalities)
  const [selectedEntity, setSelectedEntity] = useState(
    customer.entidadId ?? null
  )
  const [selectedEntityOption, setSelectedEntityOption] = useState(null)
  const [selectedMunicipalityOption, setSelectedMunicipalityOption] =
    useState(null)
  const [selectedAtcOption, setSelectedAtcOption] = useState(null)

  // ATC zone ID: comes from customer or from selected shopping area
  const atcZoneId =
    shoppingAreasSelectedMulti?.value ?? customer.id_zona_comercial_cmo ?? null

  const insert = props.insert ?? true

  const dispatch = useDispatch()

  const {
    loadingAdd,
    doneAdd,
    errorAdd,
    loadingUpdate,
    errorUpdate,
    doneUpdate,
    shoppingAreas,
  } = useSelector(state => ({
    loadingAdd: state.CustomerAdd.loading,
    errorAdd: state.CustomerAdd.error,
    doneAdd: state.CustomerAdd.done,
    loadingUpdate: state.CustomerEdit.loading,
    errorUpdate: state.CustomerEdit.error,
    doneUpdate: state.CustomerEdit.done,
    shoppingAreas: state.ShoppingAreasList.shoppingAreasV2,
  }))

  // CMO queries
  const [updateCustomerCmo] = useUpdateCustomerCmoMutation()
  const pendingCmoData = useRef(null)

  const { data: entities = [] } = useGetEntitiesCmoQuery()
  const { data: municipalities = [] } = useGetMunicipalitiesCmoQuery(
    selectedEntity,
    { skip: !selectedEntity }
  )
  const { data: atcs = [] } = useGetAtcsCmoQuery(cmoShoppingArea, {
    skip: !cmoShoppingArea,
  })

  useEffect(() => {
    if (shoppingAreas.length == 0) {
      dispatch(getShoppingAreasV2())
    }
    const shoppingArea = shoppingAreas.find(
      element => element.shoppingAreaId == customer.shoppingAreaId
    )
    if (shoppingArea !== undefined) {
      setShoppingAreasSelectedMulti({
        label: `${shoppingArea.shoppingArea}`,
        value: shoppingArea.shoppingAreaId,
      })
    }
  }, [shoppingAreas])

  // Pre-select entity from customer data once entities are loaded
  useEffect(() => {
    if (!entities.length || !customer.entidadId) return
    const entity = entities.find(e => e.id === customer.entidadId)
    if (entity) {
      setSelectedEntityOption({ label: entity.nombre, value: entity.id })
    }
  }, [entities])

  // Pre-select municipality from customer data once municipalities are loaded
  useEffect(() => {
    if (!municipalities.length || !customer.municipio) return
    const municipality = municipalities.find(m => m.id === customer.municipio)
    if (municipality) {
      setSelectedMunicipalityOption({
        label: municipality.nombre,
        value: municipality.id,
      })
    }
  }, [municipalities])

  // Pre-select ATC from customer data once ATCs are loaded
  useEffect(() => {
    if (!atcs.length || !customer.atcId) return
    const atc = atcs.find(a => a.id === customer.atcId)
    if (atc) {
      setSelectedAtcOption({ label: atc.nombre, value: atc.id })
    }
  }, [atcs])

  // Sync CMO after CELF save succeeds
  useEffect(() => {
    if ((doneAdd || doneUpdate) && pendingCmoData.current) {
      updateCustomerCmo(pendingCmoData.current)
      pendingCmoData.current = null
    }
  }, [doneAdd, doneUpdate])

  const handleSubmit = (event, errors, values) => {
    if (errors.length > 0 || shoppingAreasSelectedMulti === undefined) {
      setFormError(true)
      return
    }

    const shoppingAreaId = shoppingAreasSelectedMulti.value

    if (insert) {
      dispatch(addCustomerClean())
      const request = {
        idh: values.idh,
        name: values.name,
        businessName: values.businessName,
        municipalityId: selectedMunicipalityOption?.value ?? 0,
        entidadId: selectedEntity,
        atcId: selectedAtcOption?.value ?? null,
        stateId: 0,
        zip: "00000",
        shoppingAreaId: shoppingAreaId,
        cmoShoppingAreaId: cmoShoppingArea,
      }
      pendingCmoData.current = request
      dispatch(addCustomer(request))
    } else {
      dispatch(editCustomerClean())
      const request = {
        customerId: customer.customerId,
        idh: values.idh,
        name: values.name,
        businessName: values.businessName,
        municipalityId: selectedMunicipalityOption?.value ?? 0,
        entidadId: selectedEntity,
        atcId: selectedAtcOption?.value ?? null,
        stateId: 0,
        zip: "00000",
        shoppingAreaId: shoppingAreaId,
        status: status ? 1 : 0,
        cmoShoppingAreaId: cmoShoppingArea,
      }
      pendingCmoData.current = request
      dispatch(editCustomer(request))
    }
  }

  const handleShoppingAreasSelectedMulti = selectedMulti => {
    setShoppingAreasSelectedMulti(selectedMulti)
    // Reset ATC when shopping area changes
    setSelectedAtcOption(null)
    // Update CMO shopping area to load corresponding ATCs
    shoppingAreas.find(element => {
      if (element.shoppingAreaId == selectedMulti.value) {
        setCmoShoppingArea(element.cmoShoppingAreaId)
      }
    })
  }

  const handleEntityChange = option => {
    setSelectedEntityOption(option)
    setSelectedEntity(option?.value ?? null)
    setSelectedMunicipalityOption(null)
  }

  const shoppingAreasOG =
    shoppingAreas !== undefined
      ? shoppingAreas.map(element => {
          return {
            label: `${element.shoppingArea}`,
            value: element.shoppingAreaId,
          }
        })
      : []

  const shoppingAreasOtionGroup = [
    {
      label: "Zonas comerciales",
      options: shoppingAreasOG,
    },
  ]

  const entitiesOptions = entities.map(e => ({ label: e.nombre_entidad, value: e.id_entidad }))
  const municipalitiesOptions = municipalities.map(m => ({
    label: m.nombre_municipio,
    value: m.id_municipio,
  }))
  const atcsOptions = atcs.map(a => ({ label: `${a.nombre} ${a.apellido}`, value: a.id_usuario }))

  return (
    <AvForm className="form-horizontal" onSubmit={handleSubmit}>
      {errorAdd !== "" && (
        <div className="alert alert-danger">
          Ocurrió un error al registrar el cliente: {errorAdd}
        </div>
      )}
      {errorUpdate !== "" && (
        <div className="alert alert-danger">
          Ocurrió un error al edutar el cliente: {errorUpdate}
        </div>
      )}
      {formError && (
        <div className="alert alert-danger">
          Por favor corrija los errores en el formulario antes de continuar.
        </div>
      )}
      <Row className="mb-3">
        <Col md={3}>
          <Label className="col-form-label" for="name">
            * IDH:
          </Label>
        </Col>
        <Col md={9}>
          <AvField
            name="idh"
            value={customer.idh}
            className="form-control"
            placeholder=""
            type="text"
            validate={{
              required: {
                value: true,
                errorMessage: "Este campo es requerido",
              },
              number: {
                value: true,
                errorMessage: "Este campo debe ser numérico",
              },
            }}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Label className="col-form-label" for="name">
            * Razón social:
          </Label>
        </Col>
        <Col md={9}>
          <AvField
            name="name"
            value={customer.name}
            className="form-control"
            placeholder=""
            type="text"
            validate={{
              required: {
                value: true,
                errorMessage: "Este campo es requerido",
              },
            }}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Label className="col-form-label" for="name">
            * Nombre comercial:
          </Label>
        </Col>
        <Col md={9}>
          <AvField
            name="businessName"
            value={customer.businessName}
            className="form-control"
            placeholder=""
            type="text"
            validate={{
              required: {
                value: true,
                errorMessage: "Este campo es requerido",
              },
            }}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Label className="col-form-label" for="name">
            * Zona comercial:
          </Label>
        </Col>
        <Col md={9}>
          <Select
            name="shoppingArea"
            value={shoppingAreasSelectedMulti}
            isMulti={false}
            onChange={event => {
              handleShoppingAreasSelectedMulti(event)
            }}
            options={shoppingAreasOtionGroup}
            classNamePrefix="select2-selection"
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Label className="col-form-label">Entidad:</Label>
        </Col>
        <Col md={9}>
          <Select
            name="entidad"
            value={selectedEntityOption}
            isMulti={false}
            onChange={handleEntityChange}
            options={entitiesOptions}
            classNamePrefix="select2-selection"
            placeholder="Selecciona una entidad..."
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Label className="col-form-label">Municipio:</Label>
        </Col>
        <Col md={9}>
          <Select
            name="municipio"
            value={selectedMunicipalityOption}
            isMulti={false}
            onChange={option => setSelectedMunicipalityOption(option)}
            options={municipalitiesOptions}
            classNamePrefix="select2-selection"
            placeholder={
              selectedEntity
                ? "Selecciona un municipio..."
                : "Selecciona primero una entidad"
            }
            isDisabled={!selectedEntity}
            isClearable
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Label className="col-form-label">ATC:</Label>
        </Col>
        <Col md={9}>
          <Select
            name="atc"
            value={selectedAtcOption}
            isMulti={false}
            onChange={option => setSelectedAtcOption(option)}
            options={atcsOptions}
            classNamePrefix="select2-selection"
            placeholder={
              atcZoneId
                ? "Selecciona un ATC..."
                : "Selecciona primero una zona comercial"
            }
            isDisabled={!atcZoneId}
            isClearable
          />
        </Col>
      </Row>

      {!insert && (
        <Row className="mb-3">
          <Col md={3}>
            <Label className="col-form-label" for="name">
              * Activado:
            </Label>
          </Col>
          <Col md={9}>
            <Switch
              uncheckedIcon={<Offsymbol />}
              checkedIcon={<OnSymbol />}
              className="me-1 mb-sm-8 mb-2"
              onColor={"#ed1c2e"}
              onChange={() => {
                setStatus(!status)
              }}
              checked={status}
            />
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col xl={8} lg={8} md={6} sm={4} xs={0}></Col>
        <Col xl={2} lg={2} md={3} sm={4} xs={6}>
          <Button className={`float-right  btn-block`} type="submit">
            {!loadingAdd && !loadingUpdate && <span>Guardar</span>}
            {(loadingAdd || loadingUpdate) && <span>Guardando...</span>}
          </Button>
        </Col>
        <Col xl={2} lg={2} md={3} sm={4} xs={6}>
          <button
            type="button"
            onClick={() => props.onCancel()}
            className="btn btn-light btn-block"
          >
            Cancelar
          </button>
        </Col>
      </Row>
      {doneAdd && insert && errorAdd === "" && (
        <SweetAlert
          title="Cliente registrado exitosamente"
          success
          confirmBtnBsStyle="success"
          onConfirm={() => {
            props.onDone()
          }}
        >
          <p>El cliente fue registrado exitosamente.</p>
        </SweetAlert>
      )}
      {doneUpdate && !insert && errorUpdate === "" && (
        <SweetAlert
          title="Cliente actualizado exitosamente"
          success
          confirmBtnBsStyle="success"
          onConfirm={() => {
            props.onDone()
          }}
        >
          <p>El cliente fue actualizado exitosamente.</p>
        </SweetAlert>
      )}
      <style jsx="true">{`
        .radio-sublabel {
          color: gray;
          padding-left: 2rem;
        }
        .users-group {
          padding-left: 3rem;
        }
      `}</style>
    </AvForm>
  )
}

CustomersForm.propTypes = {
  insert: PropTypes.bool,
  customer: PropTypes.object,
  history: PropTypes.object,
  onDone: PropTypes.func,
  onCancel: PropTypes.func,
}

export default withRouter(CustomersForm)
